import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { reservationSecondsLeft, shownStatus, streamName, type Stream } from '../api';
import { useStreams } from '../queries';
import { Icon, StatusBadge, Thumb, clock, endReason, kbps, recordingLine, useNow, when } from '../ui';

type Tab = 'all' | 'live' | 'pending' | 'ended';
const FINISHED: Stream['status'][] = ['ended', 'expired', 'cancelled'];

function statusNote(s: Stream, left: number | null): string {
  if (s.status === 'live') {
    const watching = s.viewers_now ? ` · ${s.viewers_now.toLocaleString()} watching` : '';
    return `${s.ingest ? 'Receiving' : 'Connecting'}${watching || (s.record ? ' · recording' : '')}`;
  }
  if (left === 0) return endReason('reservation_ttl');
  if (left !== null) return left < 300 ? `Expires in ${clock(left)}` : `Expires in ${Math.ceil(left / 60)} min`;
  return endReason(s.end_reason);
}

function ingestLine(s: Stream): string {
  if (s.ingest?.width && s.ingest.height) {
    const size = `${s.ingest.width}×${s.ingest.height}`;
    return s.status === 'live' ? `${size} · ${kbps(s.ingest.kbps)}` : `${size} · declared ${s.max_ingest_resolution}p`;
  }
  return `Declared ${s.max_ingest_resolution}p`;
}

function duration(s: Stream, now: number): string {
  if (!s.went_live_at) return '—';
  const end = s.ended_at ? new Date(s.ended_at).getTime() : now;
  return clock((end - new Date(s.went_live_at).getTime()) / 1000);
}

export function Streams() {
  const q = useStreams();
  const now = useNow(1000);
  const [tab, setTab] = useState<Tab>('all');
  const all = q.data?.data ?? [];
  const count = (t: Tab) =>
    t === 'all' ? all.length : t === 'ended' ? all.filter((s) => FINISHED.includes(s.status)).length : all.filter((s) => s.status === t).length;
  const rows = tab === 'all' ? all : tab === 'ended' ? all.filter((s) => FINISHED.includes(s.status)) : all.filter((s) => s.status === tab);

  return (
    <section className="page">
      <div className="page-head">
        <div>
          <h1>Streams</h1>
          <p className="sub">Every stream you create, from here or the API.</p>
        </div>
        <Link to="/streams/new" className="btn primary">
          <Icon name="plus" size={14} width={2} />
          New stream
        </Link>
      </div>

      <div className="tabs" role="tablist">
        {(['all', 'live', 'pending', 'ended'] as Tab[]).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`tab${tab === t ? ' on' : ''}`} onClick={() => setTab(t)}>
            {{ all: 'All', live: 'Live', pending: 'Waiting', ended: 'Ended' }[t]}
            <span className="c">{count(t)}</span>
          </button>
        ))}
      </div>

      {q.isPending ? (
        <p className="muted">Loading…</p>
      ) : q.error ? (
        <p className="error-text">{q.error.message}</p>
      ) : rows.length === 0 ? (
        <div className="table">
          <div className="empty">
            <Icon name="video" size={28} width={1.2} />
            {all.length === 0 ? (
              <>
                <span>No streams yet. Create one and you'll have an RTMP address and an embed code in a second.</span>
                <Link to="/streams/new" className="btn primary">
                  New stream
                </Link>
              </>
            ) : (
              <span>Nothing here.</span>
            )}
          </div>
        </div>
      ) : (
        <div className="table" role="table" aria-label="Streams">
          <div className="tr th" role="row">
            <span role="columnheader">Stream</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Ingest</span>
            <span role="columnheader">Started</span>
            <span role="columnheader">Duration</span>
            <span role="columnheader">Recording</span>
          </div>
          {rows.map((s) => {
            const left = reservationSecondsLeft(s, q.dataUpdatedAt, now);
            return (
            <Link key={s.id} to="/streams/$id" params={{ id: s.id }} className="tr" role="row">
              <span className="name with-thumb" role="cell">
                <Thumb stream={s} />
                <div>
                  <b>{streamName(s)}</b>
                  <span>{s.id}</span>
                </div>
              </span>
              <span className="st" role="cell">
                <StatusBadge status={shownStatus(s, left)} />
                <span>{statusNote(s, left)}</span>
              </span>
              <span className="cell-mono" role="cell">{ingestLine(s)}</span>
              <span className="muted" role="cell">{when(s.went_live_at, now)}</span>
              <span className="mono" role="cell">{duration(s, now)}</span>
              <span className="muted" role="cell">{recordingLine(s, now)}</span>
            </Link>
            );
          })}
        </div>
      )}
      {q.data?.has_more && <p className="faint" style={{ margin: 0, fontSize: 12 }}>Showing the latest 100 streams.</p>}
    </section>
  );
}
